import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Network, ChevronRight, ChevronDown, Plus, Edit3, Trash2,
  ArrowUp, ArrowDown, Save, X, Tag, Users, Download, Search, FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// =====================================================================
// PANNELLO COMBINATO: Organigramma + Gestione Qualifiche
// =====================================================================
export default function OrganigrammaPanel({ open, onClose, currentUser }) {
  const [tab, setTab] = useState('organigramma');

  if (!open) return null;
  const isAdmin = currentUser?.role === 'amministratore' || currentUser?.role === 'super_amministratore';
  const isSuperAdmin = currentUser?.role === 'super_amministratore';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="organigramma-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Network className="h-5 w-5 text-cyan-600" />
            Organigramma & Qualifiche
          </DialogTitle>
        </DialogHeader>

        {/* Tab nav */}
        <div className="flex gap-1 border-b border-gray-200 -mt-2">
          <TabButton active={tab === 'organigramma'} onClick={() => setTab('organigramma')} icon={Network} label="Organigramma" />
          <TabButton active={tab === 'qualifiche'} onClick={() => setTab('qualifiche')} icon={Tag} label="Qualifiche" />
          {isSuperAdmin && (
            <TabButton active={tab === 'contratti'} onClick={() => setTab('contratti')} icon={FileText} label="Contratti" />
          )}
        </div>

        <div className="mt-4">
          {tab === 'organigramma' && <OrganigrammaTree isAdmin={isAdmin} />}
          {tab === 'qualifiche' && <QualificheManager isAdmin={isAdmin} isSuperAdmin={isSuperAdmin} />}
          {tab === 'contratti' && isSuperAdmin && <ContrattiManager />}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition ${
      active ? 'border-cyan-600 text-cyan-700' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
    data-testid={`tab-${label.toLowerCase()}`}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

// =====================================================================
// ALBERO ORGANIGRAMMA (con filtri + visual restyle)
// =====================================================================
function OrganigrammaTree({ isAdmin }) {
  const [nodes, setNodes] = useState([]);
  const [qualOrdine, setQualOrdine] = useState({}); // { 'Direttore Sanitario': 0, ... }
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  // Filtri
  const [filterAsl, setFilterAsl] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterQualifica, setFilterQualifica] = useState('');
  const [search, setSearch] = useState('');

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const [resTree, resQual] = await Promise.all([
        axios.get(`${API}/users/organigramma`),
        axios.get(`${API}/qualifiche`).catch(() => ({ data: [] })),
      ]);
      setNodes(resTree.data || []);
      const map = {};
      (resQual.data || []).forEach((q, idx) => {
        const ord = q.ordine !== undefined && q.ordine !== null ? q.ordine : (idx + 1) * 10;
        if (q.nome) map[q.nome.toLowerCase()] = ord;
      });
      setQualOrdine(map);
    } catch (e) {
      toast.error('Errore caricamento organigramma');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  // Costruisci dropdown options
  const aslOptions = useMemo(() => {
    const s = new Set();
    nodes.forEach(n => { if (n.asl_riferimento) s.add(n.asl_riferimento); });
    return Array.from(s).sort();
  }, [nodes]);
  const qualificheOptions = useMemo(() => {
    const s = new Set();
    nodes.forEach(n => (n.qualifiche || []).forEach(q => s.add(q)));
    return Array.from(s).sort();
  }, [nodes]);

  // Filtra nodi: mantieni anche gli antenati per non rompere la gerarchia
  const filtered = useMemo(() => {
    const hasFilter = !!(filterAsl || filterRole || filterQualifica || search);
    if (!hasFilter) return nodes;
    const byId = new Map(nodes.map(n => [n.id, n]));
    const matches = (n) => {
      if (filterAsl && (n.asl_riferimento || '') !== filterAsl) return false;
      if (filterRole && (n.role || '') !== filterRole) return false;
      if (filterQualifica && !(n.qualifiche || []).includes(filterQualifica)) return false;
      if (search) {
        const s = search.toLowerCase();
        const hay = [
          n.full_name, n.figura_professionale, n.asl_riferimento,
          (n.qualifiche || []).join(' ')
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    };
    const keep = new Set();
    for (const n of nodes) {
      if (matches(n)) {
        let cur = n; let hops = 0;
        while (cur && hops < 50) {
          keep.add(cur.id);
          if (!cur.manager_id || !byId.has(cur.manager_id)) break;
          cur = byId.get(cur.manager_id);
          hops += 1;
        }
      }
    }
    return nodes.filter(n => keep.has(n.id));
  }, [nodes, filterAsl, filterRole, filterQualifica, search]);

  const { roots, childrenMap } = useMemo(() => {
    const byId = new Map(filtered.map(n => [n.id, n]));
    const childrenMap = new Map();
    const roots = [];
    for (const n of filtered) {
      const pid = n.manager_id;
      if (pid && byId.has(pid)) {
        if (!childrenMap.has(pid)) childrenMap.set(pid, []);
        childrenMap.get(pid).push(n);
      } else {
        roots.push(n);
      }
    }
    // Rank di un utente = min ordine delle sue qualifiche; nessuna => INF (in fondo)
    const rankOf = (n) => {
      const quals = n.qualifiche || [];
      let best = Number.POSITIVE_INFINITY;
      for (const q of quals) {
        const ord = qualOrdine[(q || '').toLowerCase()];
        if (typeof ord === 'number' && ord < best) best = ord;
      }
      return best;
    };
    const sortFn = (a, b) => {
      const ra = rankOf(a), rb = rankOf(b);
      if (ra !== rb) return ra - rb;
      return (a.full_name || '').localeCompare(b.full_name || '');
    };
    roots.sort(sortFn);
    for (const arr of childrenMap.values()) arr.sort(sortFn);
    return { roots, childrenMap };
  }, [filtered, qualOrdine]);

  const toggle = (id) => setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  const expandAll = () => setCollapsed({});
  const collapseAll = () => {
    const all = {};
    for (const n of filtered) {
      if ((childrenMap.get(n.id) || []).length > 0) all[n.id] = true;
    }
    setCollapsed(all);
  };
  const resetFilters = () => {
    setFilterAsl(''); setFilterRole(''); setFilterQualifica(''); setSearch('');
  };

  const updateManager = async (userId, newManagerId) => {
    try {
      await axios.put(`${API}/users/${userId}`, { manager_id: newManagerId || '' });
      toast.success(newManagerId ? 'Responsabile aggiornato' : 'Responsabile rimosso');
      fetchTree();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore aggiornamento');
    }
  };

  const handleExportPdf = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filterAsl) params.set('asl', filterAsl);
      if (filterRole) params.set('role', filterRole);
      if (filterQualifica) params.set('qualifica', filterQualifica);
      if (search) params.set('search', search);
      const qs = params.toString();
      const url = `${API}/users/organigramma/pdf${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `organigramma_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Organigramma esportato');
    } catch (e) {
      toast.error('Errore export PDF');
    }
  };

  const hasActiveFilters = !!(filterAsl || filterRole || filterQualifica || search);

  if (loading) return <div className="text-center py-8 text-sm text-gray-500">Caricamento…</div>;
  if (!nodes.length) return <div className="text-center py-8 text-sm text-gray-500">Nessun utente</div>;

  return (
    <div className="space-y-3" data-testid="organigramma-tree">
      {/* Filter bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2.5">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per nome, qualifica, figura…"
              className="h-9 text-xs pl-8"
              data-testid="organigramma-search"
            />
          </div>
          <select
            value={filterAsl}
            onChange={(e) => setFilterAsl(e.target.value)}
            className="h-9 px-2 text-xs rounded-md border border-gray-200 bg-white min-w-[120px]"
            data-testid="organigramma-filter-asl"
          >
            <option value="">Tutte le ASL</option>
            {aslOptions.map(a => {
              const clean = a.replace(/^asl\s+/i, '');
              return <option key={a} value={a}>ASL {clean}</option>;
            })}
          </select>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="h-9 px-2 text-xs rounded-md border border-gray-200 bg-white min-w-[120px]"
            data-testid="organigramma-filter-role"
          >
            <option value="">Tutti i ruoli</option>
            <option value="super_amministratore">Super Amministratore</option>
            <option value="amministratore">Amministratore</option>
            <option value="operatore">Operatore</option>
          </select>
          <select
            value={filterQualifica}
            onChange={(e) => setFilterQualifica(e.target.value)}
            className="h-9 px-2 text-xs rounded-md border border-gray-200 bg-white min-w-[140px]"
            data-testid="organigramma-filter-qualifica"
          >
            <option value="">Tutte le qualifiche</option>
            {qualificheOptions.map(q => <option key={q} value={q}>{q}</option>)}
          </select>
          {hasActiveFilters && (
            <Button onClick={resetFilters} variant="ghost" size="sm" className="h-9 text-xs gap-1 text-gray-500 hover:text-gray-700" data-testid="organigramma-reset-filters">
              <X className="h-3 w-3" /> Reset
            </Button>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap text-[11px]">
          <div className="flex items-center gap-3 text-gray-500">
            <button onClick={expandAll} className="hover:text-cyan-700 underline-offset-2 hover:underline" data-testid="organigramma-expand-all">Espandi tutto</button>
            <button onClick={collapseAll} className="hover:text-cyan-700 underline-offset-2 hover:underline" data-testid="organigramma-collapse-all">Comprimi tutto</button>
            <span data-testid="organigramma-stats">
              <strong className="text-gray-900">{filtered.length}</strong> utent{filtered.length === 1 ? 'e' : 'i'}{hasActiveFilters && ` su ${nodes.length}`} · <strong className="text-gray-900">{roots.length}</strong> radic{roots.length === 1 ? 'e' : 'i'}
            </span>
          </div>
          <Button
            onClick={handleExportPdf}
            variant="outline"
            size="sm"
            className="h-7 gap-1.5 text-[11px] border-gray-300 text-gray-700 hover:bg-gray-50"
            data-testid="organigramma-export-pdf"
          >
            <Download className="h-3 w-3" />
            Esporta PDF{hasActiveFilters ? ' filtrato' : ''}
          </Button>
        </div>
      </div>

      {/* Tree (flat, ordinato alfabeticamente; gerarchia rispettata via manager_id) */}
      <div className="bg-white rounded-lg p-2 border border-gray-200 max-h-[55vh] overflow-y-auto">
        {roots.length === 0 ? (
          <div className="text-center py-8 text-sm text-gray-500">
            Nessun utente corrisponde ai filtri selezionati.
            {hasActiveFilters && <button onClick={resetFilters} className="ml-1 text-cyan-600 hover:underline">Resetta i filtri</button>}
          </div>
        ) : roots.map((root, idx) => (
          <TreeNode key={root.id} node={root} level={0} childrenMap={childrenMap}
                    collapsed={collapsed} toggle={toggle} isAdmin={isAdmin}
                    nodes={filtered} updateManager={updateManager}
                    isLast={idx === roots.length - 1} parentPath={[]} />
        ))}
      </div>
    </div>
  );
}

// Restituisce le iniziali (max 2 char)
const _initials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Hash deterministico per scegliere un colore avatar dal nome
const _avatarColor = (name) => {
  const palette = [
    { bg: 'bg-rose-100', text: 'text-rose-700' },
    { bg: 'bg-orange-100', text: 'text-orange-700' },
    { bg: 'bg-amber-100', text: 'text-amber-700' },
    { bg: 'bg-emerald-100', text: 'text-emerald-700' },
    { bg: 'bg-teal-100', text: 'text-teal-700' },
    { bg: 'bg-cyan-100', text: 'text-cyan-700' },
    { bg: 'bg-blue-100', text: 'text-blue-700' },
    { bg: 'bg-indigo-100', text: 'text-indigo-700' },
    { bg: 'bg-violet-100', text: 'text-violet-700' },
    { bg: 'bg-pink-100', text: 'text-pink-700' },
  ];
  let h = 0;
  for (const c of (name || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return palette[h % palette.length];
};

function TreeNode({ node, level, childrenMap, collapsed, toggle, isAdmin, nodes, updateManager, isLast, parentPath }) {
  const children = childrenMap.get(node.id) || [];
  const hasChildren = children.length > 0;
  const isCollapsed = collapsed[node.id];
  const [editingManager, setEditingManager] = useState(false);
  const [pendingMgrId, setPendingMgrId] = useState(node.manager_id || '');

  const avatar = _avatarColor(node.full_name);

  const descendants = useMemo(() => {
    const desc = new Set();
    const walk = (id) => {
      desc.add(id);
      const ch = childrenMap.get(id) || [];
      ch.forEach(c => walk(c.id));
    };
    walk(node.id);
    return desc;
  }, [node.id, childrenMap]);
  const eligibleManagers = nodes.filter(n => !descendants.has(n.id));

  // Connettori verticali per i livelli ancestrali (parentPath: array di booleans isLast per ogni avo)
  const renderConnectors = () => {
    if (level === 0) return null;
    return (
      <div className="absolute left-0 top-0 bottom-0 flex">
        {parentPath.map((wasLast, i) => (
          <div key={i} className="w-6 relative">
            {!wasLast && <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />}
          </div>
        ))}
        {/* Linea L per il nodo corrente */}
        <div className="w-6 relative">
          <div className={`absolute left-3 top-0 ${isLast ? 'h-1/2' : 'bottom-0'} w-px bg-gray-200`} style={!isLast ? {} : { height: '20px' }} />
          <div className="absolute left-3 top-[20px] w-3 h-px bg-gray-200" />
        </div>
      </div>
    );
  };

  const aslClean = node.asl_riferimento ? node.asl_riferimento.replace(/^asl\s+/i, '') : '';

  return (
    <div data-testid={`tree-node-${node.id}`}>
      <div className="relative" style={{ paddingLeft: `${level * 24}px` }}>
        {renderConnectors()}
        <div className="relative flex items-center gap-2 my-1 py-2 pr-2 pl-2.5 bg-white rounded-md border border-gray-200 hover:border-cyan-300 transition-colors group">
          {/* Toggle expand */}
          <button
            onClick={() => hasChildren && toggle(node.id)}
            className={`w-5 h-5 flex items-center justify-center shrink-0 ${hasChildren ? 'text-gray-400 hover:text-cyan-600' : 'text-transparent'}`}
            disabled={!hasChildren}
          >
            {hasChildren ? (isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />) : <span className="w-1 h-1 rounded-full bg-gray-200" />}
          </button>

          {/* Avatar */}
          <div className={`w-8 h-8 rounded-full ${avatar.bg} ${avatar.text} flex items-center justify-center font-semibold text-[11px] shrink-0`}>
            {_initials(node.full_name)}
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-sm font-medium text-gray-900 truncate">{node.full_name}</span>
              {node.is_coordinatore && (
                <span className="inline-flex items-center text-amber-500" title="Coordinatore">★</span>
              )}
              {hasChildren && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-500 border border-gray-200">
                  {children.length} riport{children.length === 1 ? 'o' : 'i'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap text-[11px] text-gray-500">
              {node.figura_professionale && (
                <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{node.figura_professionale}</span>
              )}
              {aslClean && (
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-1 h-1 rounded-full bg-gray-300" />
                  ASL {aslClean}
                </span>
              )}
              {(node.qualifiche || []).map((q) => (
                <span key={q} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-700 border border-gray-200">
                  {q}
                </span>
              ))}
            </div>
          </div>

          {/* Actions */}
          {isAdmin && (
            editingManager ? (
              <div className="flex items-center gap-1 shrink-0">
                <select
                  value={pendingMgrId}
                  onChange={(e) => setPendingMgrId(e.target.value)}
                  className="h-7 text-xs px-1 rounded border border-gray-300 bg-white max-w-[160px]"
                  data-testid={`tree-mgr-select-${node.id}`}
                >
                  <option value="">— Nessuno —</option>
                  {eligibleManagers.map(m => (
                    <option key={m.id} value={m.id}>{m.full_name}</option>
                  ))}
                </select>
                <button onClick={async () => { await updateManager(node.id, pendingMgrId); setEditingManager(false); }}
                        className="h-7 px-1.5 rounded bg-emerald-600 text-white text-xs hover:bg-emerald-700"
                        data-testid={`tree-mgr-save-${node.id}`}>
                  <Save className="h-3 w-3" />
                </button>
                <button onClick={() => { setEditingManager(false); setPendingMgrId(node.manager_id || ''); }}
                        className="h-7 px-1.5 rounded bg-gray-200 text-gray-700 text-xs hover:bg-gray-300">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setPendingMgrId(node.manager_id || ''); setEditingManager(true); }}
                className="h-7 w-7 flex items-center justify-center text-gray-400 hover:text-cyan-600 hover:bg-gray-50 rounded transition opacity-0 group-hover:opacity-100 shrink-0"
                title="Cambia responsabile"
                data-testid={`tree-mgr-edit-${node.id}`}
              >
                <Edit3 className="h-3.5 w-3.5" />
              </button>
            )
          )}
        </div>
      </div>
      {hasChildren && !isCollapsed && (
        <div>
          {children.map((child, idx) => (
            <TreeNode key={child.id} node={child} level={level + 1} childrenMap={childrenMap}
                      collapsed={collapsed} toggle={toggle} isAdmin={isAdmin}
                      nodes={nodes} updateManager={updateManager}
                      isLast={idx === children.length - 1}
                      parentPath={[...parentPath, isLast]} />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// GESTIONE QUALIFICHE (CRUD + reorder)
// =====================================================================
function QualificheManager({ isAdmin, isSuperAdmin }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // {id, nome}

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/qualifiche`);
      setItems(res.data || []);
    } catch {
      toast.error('Errore caricamento qualifiche');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreate = async () => {
    const nome = newName.trim();
    if (!nome) return;
    try {
      const res = await axios.post(`${API}/qualifiche`, { nome });
      if (res.data?.duplicate) toast.info('Qualifica già esistente');
      else toast.success('Qualifica creata');
      setNewName('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore creazione');
    }
  };

  const handleRename = async () => {
    const nome = (editing?.nome || '').trim();
    if (!nome || !editing?.id) return;
    try {
      const res = await axios.put(`${API}/qualifiche/${editing.id}`, { nome });
      const u = res.data?.users_updated || 0;
      toast.success(`Qualifica rinominata${u ? ` · ${u} utenti aggiornati` : ''}`);
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore rinomina');
    }
  };

  const handleDelete = async (q) => {
    if (q.is_default) return toast.error('Le qualifiche di default non possono essere eliminate');
    if (!window.confirm(`Eliminare la qualifica "${q.nome}"? Verra' rimossa da tutti gli utenti che la avevano.`)) return;
    try {
      await axios.delete(`${API}/qualifiche/${q.id}`);
      toast.success('Qualifica eliminata');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const moveItem = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = items.slice();
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setItems(next); // ottimistica
    try {
      await axios.put(`${API}/qualifiche/reorder`, { order: next.map(i => i.id) });
    } catch (e) {
      toast.error('Errore riordino');
      fetchAll();
    }
  };

  if (loading) return <div className="text-center py-8 text-sm text-gray-500">Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="qualifiche-manager">
      {/* Add */}
      {isAdmin && (
        <div className="flex gap-2 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder='Nuova qualifica (es. "Direttore Marketing")'
            maxLength={120}
            className="h-9 text-sm"
            data-testid="qualifica-new-input"
          />
          <Button onClick={handleCreate} size="sm" disabled={!newName.trim()} data-testid="qualifica-create-btn">
            <Plus className="h-4 w-4 mr-1" />Aggiungi
          </Button>
        </div>
      )}

      {/* List */}
      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
        {items.map((q, idx) => (
          <div key={q.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 group" data-testid={`qualifica-row-${q.id}`}>
            <span className="text-xs text-gray-400 w-6 text-right">{idx + 1}.</span>
            {editing?.id === q.id ? (
              <>
                <Input
                  value={editing.nome}
                  onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(null); }}
                  className="h-8 text-sm flex-1"
                  autoFocus
                  data-testid={`qualifica-edit-input-${q.id}`}
                />
                <button onClick={handleRename} className="p-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700">
                  <Save className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => setEditing(null)} className="p-1.5 rounded bg-gray-200 text-gray-700 hover:bg-gray-300">
                  <X className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-gray-800">{q.nome}</span>
                {q.is_default && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">default</span>
                )}
                {isAdmin && (
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    <button onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                            title="Sposta su"
                            data-testid={`qualifica-up-${q.id}`}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => moveItem(idx, +1)} disabled={idx === items.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                            title="Sposta giù"
                            data-testid={`qualifica-down-${q.id}`}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => setEditing({ id: q.id, nome: q.nome })}
                            className="p-1 text-gray-400 hover:text-cyan-600"
                            title="Rinomina"
                            data-testid={`qualifica-edit-${q.id}`}>
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                    {isSuperAdmin && !q.is_default && (
                      <button onClick={() => handleDelete(q)}
                              className="p-1 text-red-400 hover:text-red-600"
                              title="Elimina"
                              data-testid={`qualifica-delete-${q.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {!items.length && (
          <div className="text-center py-6 text-sm text-gray-500">Nessuna qualifica configurata</div>
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        Le qualifiche di default sono protette dall'eliminazione. Rinominandone una, l'aggiornamento si propaga a tutti gli utenti che la avevano assegnata.
        {isAdmin && ' Usa le frecce per riordinare l\'elenco.'}
      </p>
    </div>
  );
}

// =====================================================================
// GESTIONE CONTRATTI PROFESSIONALI (CRUD modelli + ore per turno)
// =====================================================================
const TURNO_SIGLE_DEFAULT = ['M', 'P', 'N', 'P/N', 'REP', 'R'];

function ContrattiManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = {
    nome: '', descrizione: '',
    ore_mensili: '', ore_settimanali: '', ore_giornaliere: '',
    giorni_settimana: 6, is_libero_professionista: false,
    ore_per_turno: {}, ordine: 100,
  };
  const [form, setForm] = useState(empty);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contratti-tipo`);
      setItems(res.data || []);
    } catch {
      toast.error('Errore caricamento contratti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nome: c.nome || '', descrizione: c.descrizione || '',
      ore_mensili: c.ore_mensili ?? '',
      ore_settimanali: c.ore_settimanali ?? '',
      ore_giornaliere: c.ore_giornaliere ?? '',
      giorni_settimana: c.giorni_settimana ?? 6,
      is_libero_professionista: !!c.is_libero_professionista,
      ore_per_turno: { ...(c.ore_per_turno || {}) },
      ordine: c.ordine ?? 100,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error('Nome obbligatorio'); return; }
    const toNum = (v) => v === '' || v === null || v === undefined ? null : parseFloat(v);
    const payload = {
      nome: form.nome.trim(),
      descrizione: form.descrizione || null,
      ore_mensili: toNum(form.ore_mensili),
      ore_settimanali: toNum(form.ore_settimanali),
      ore_giornaliere: toNum(form.ore_giornaliere),
      giorni_settimana: parseInt(form.giorni_settimana, 10) || 6,
      is_libero_professionista: !!form.is_libero_professionista,
      ore_per_turno: form.ore_per_turno || {},
      ordine: parseInt(form.ordine, 10) || 100,
    };
    try {
      if (editing) {
        await axios.put(`${API}/contratti-tipo/${editing.id}`, payload);
        toast.success('Contratto aggiornato');
      } else {
        await axios.post(`${API}/contratti-tipo`, payload);
        toast.success('Contratto creato');
      }
      setShowForm(false); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    }
  };

  const handleDelete = async (c) => {
    if (c.is_default) return toast.error('I contratti di default non possono essere eliminati');
    if (!window.confirm(`Eliminare il contratto "${c.nome}"? Gli utenti che lo usano perderanno il riferimento (manterranno gli override individuali).`)) return;
    try {
      await axios.delete(`${API}/contratti-tipo/${c.id}`);
      toast.success('Contratto eliminato'); fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  if (loading) return <div className="text-center py-8 text-sm text-gray-500">Caricamento…</div>;

  if (showForm) {
    return (
      <div className="space-y-3" data-testid="contratti-form">
        <div className="flex items-center justify-between border-b border-gray-200 pb-2">
          <h3 className="text-sm font-semibold text-gray-700">{editing ? 'Modifica contratto' : 'Nuovo contratto'}</h3>
          <button onClick={() => setShowForm(false)} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <X className="h-3.5 w-3.5" /> Annulla
          </button>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <div>
            <Label className="text-xs">Nome *</Label>
            <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                   placeholder='es. "CCNL Cooperative Sociali Full-Time"' className="h-9 text-sm"
                   data-testid="contratto-form-nome" />
          </div>
          <div>
            <Label className="text-xs">Descrizione</Label>
            <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
                   placeholder='es. "165 ore/mese · 38 ore/sett · Lun-Sab"' className="h-9 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Ore mensili</Label>
              <Input type="number" min="0" step="0.01" value={form.ore_mensili}
                     onChange={(e) => setForm({ ...form, ore_mensili: e.target.value })} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Ore settimanali</Label>
              <Input type="number" min="0" step="0.01" value={form.ore_settimanali}
                     onChange={(e) => setForm({ ...form, ore_settimanali: e.target.value })} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Ore giornaliere</Label>
              <Input type="number" min="0" step="0.01" value={form.ore_giornaliere}
                     onChange={(e) => setForm({ ...form, ore_giornaliere: e.target.value })} className="h-9 text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Giorni settimanali</Label>
              <select value={form.giorni_settimana} onChange={(e) => setForm({ ...form, giorni_settimana: e.target.value })}
                      className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white">
                <option value={5}>5 giorni (Lun-Ven)</option>
                <option value={6}>6 giorni (Lun-Sab)</option>
                <option value={7}>7 giorni</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-gray-700 pb-2">
                <input type="checkbox" checked={form.is_libero_professionista}
                       onChange={(e) => setForm({ ...form, is_libero_professionista: e.target.checked })}
                       className="w-4 h-4" />
                Libero professionista
              </label>
            </div>
          </div>

          <div className="border border-amber-200 bg-amber-50 rounded-md p-3">
            <Label className="text-xs font-semibold text-amber-900">Ore per sigla turno</Label>
            <p className="text-[11px] text-amber-700 mt-0.5 mb-2">
              Specifica le ore che ogni turno (M/P/N…) vale per chi ha questo contratto. Le sigle non specificate ricadono sulla durata della legenda turni.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {TURNO_SIGLE_DEFAULT.map(sigla => (
                <div key={sigla}>
                  <Label className="text-[11px]">{sigla}</Label>
                  <Input type="number" min="0" step="0.01"
                         value={form.ore_per_turno[sigla] ?? ''}
                         onChange={(e) => {
                           const v = e.target.value;
                           const next = { ...form.ore_per_turno };
                           if (v === '') delete next[sigla];
                           else next[sigla] = parseFloat(v);
                           setForm({ ...form, ore_per_turno: next });
                         }}
                         className="h-8 text-sm" />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setShowForm(false)}>Annulla</Button>
          <Button size="sm" onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="contratto-save-btn">
            <Save className="h-3.5 w-3.5 mr-1" /> Salva
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="contratti-manager">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Modelli di contratto riusabili. Definisci ore mensili/settimanali/giornaliere e ore per ogni sigla turno.
        </p>
        <Button size="sm" onClick={openNew} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="contratto-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Nuovo
        </Button>
      </div>

      <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 bg-white">
        {items.map((c) => (
          <div key={c.id} className="px-3 py-2.5 hover:bg-gray-50 group" data-testid={`contratto-row-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-gray-900">{c.nome}</span>
                  {c.is_libero_professionista && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">Libero Prof.</span>
                  )}
                  {c.is_default && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200">default</span>
                  )}
                </div>
                {c.descrizione && <p className="text-xs text-gray-500 mt-0.5">{c.descrizione}</p>}
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-600 flex-wrap">
                  {c.ore_mensili != null && <span><strong>{c.ore_mensili}</strong> h/mese</span>}
                  {c.ore_settimanali != null && <span><strong>{c.ore_settimanali}</strong> h/sett</span>}
                  {c.ore_giornaliere != null && <span><strong>{c.ore_giornaliere}</strong> h/die</span>}
                  <span>{c.giorni_settimana || 6} gg/sett</span>
                  {Object.keys(c.ore_per_turno || {}).length > 0 && (
                    <span className="text-amber-700">
                      Turni: {Object.entries(c.ore_per_turno).map(([s, v]) => `${s}=${v}h`).join(' · ')}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition shrink-0">
                <button onClick={() => openEdit(c)} className="p-1.5 text-gray-400 hover:text-cyan-600 hover:bg-cyan-50 rounded"
                        title="Modifica" data-testid={`contratto-edit-${c.id}`}>
                  <Edit3 className="h-3.5 w-3.5" />
                </button>
                {!c.is_default && (
                  <button onClick={() => handleDelete(c)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Elimina" data-testid={`contratto-delete-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="text-center py-6 text-sm text-gray-500">Nessun contratto configurato</div>
        )}
      </div>

      <p className="text-[11px] text-gray-500">
        I contratti di default (Libero Professionista, CCNL Coop Sociali Full/Part-Time) sono protetti dall'eliminazione. Per ogni utente puoi assegnare un contratto e/o sovrascrivere singoli valori dal pannello "Modifica utente".
      </p>
    </div>
  );
}
